import type { ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Link from "@/v2-backoffice-client/components/link"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import { formatTrendDetail } from "@/v2-backoffice-client/usage/usage.constants"
import { mcpTrendPct } from "@/v2-backoffice-client/usage/mcp/usage.mcp.utils"
import type { UsageLicenseDeniedCustomerQuery } from "@/v2-backoffice-client/usage/denied/__types__/usage.deniedCustomer.query"
import { formatLicenseDeniedFeature } from "@/v2-backoffice-client/usage/denied/usage.denied.utils"

export type UsageLicenseDeniedMember =
  UsageLicenseDeniedCustomerQuery["getUsageLicenseDeniedByCustomer"]["Members"][number]

export type UsageLicenseDeniedMembersTableProps = ComponentProps<"div"> & {
  members: UsageLicenseDeniedMember[]
}

function memberName(member: UsageLicenseDeniedMember) {
  const name = `${member.FirstName ?? ""} ${member.LastName ?? ""}`.trim()
  return name || member.Email || `User ${member.UserId}`
}

// Who at the customer is asking for more than the license carries: one row
// per person with 28-day refused attempts and trend, how many features they
// tried, which ones, and when last. Most attempts first — the person to
// name in the upsell conversation sits on top. ">>" pushes the user panel
// onto the sheet stack so back returns here.
function UsageLicenseDeniedMembersTable(
  props: UsageLicenseDeniedMembersTableProps,
) {
  const { members, className, ...divProps } = props

  return (
    <div className={cn("overflow-x-auto", className)} {...divProps}>
      <table className="usage-report-table">
        <thead>
          <tr>
            <th>Person</th>
            <th className="num">Attempts · 28 d</th>
            <th className="num">Features</th>
            <th>Tried most</th>
            <th>Last</th>
          </tr>
        </thead>
        <tbody>
          {members.map((member) => (
            <tr key={member.UserId}>
              <td>
                <Link
                  variant="muted"
                  size="xs"
                  iconSrc={null}
                  to={`>>?v=user:${member.UserId}&user-view-tabs-tab=usage`}
                  className="font-medium text-foreground underline-offset-2 hover:underline"
                >
                  {memberName(member)}
                </Link>
                {member.Email && (
                  <span className="block max-w-56 truncate text-muted-foreground">
                    {member.Email}
                  </span>
                )}
              </td>
              <td className="num">
                <span className="inline-flex items-center justify-end gap-1.5">
                  {member.Attempts28d > 0
                    ? formatNumber(member.Attempts28d)
                    : "—"}
                  {(member.Attempts28d > 0 || member.AttemptsPrev28d > 0) && (
                    <UsageTrendBadge
                      trendPct={mcpTrendPct(
                        member.Attempts28d,
                        member.AttemptsPrev28d,
                      )}
                      detail={formatTrendDetail(
                        member.Attempts28d,
                        member.AttemptsPrev28d,
                      )}
                    />
                  )}
                </span>
              </td>
              <td className="num">
                {member.Features28d > 0 ? member.Features28d : "—"}
              </td>
              <td>
                {member.TopFeatures.length > 0
                  ? member.TopFeatures.map(
                      (feature) =>
                        `${formatLicenseDeniedFeature(feature.FeatureCode, feature.FeatureName)} (${formatNumber(feature.Attempts)})`,
                    ).join(", ")
                  : "—"}
              </td>
              <td className="whitespace-nowrap text-muted-foreground">
                {member.LastAt ? <RelativeDate date={member.LastAt} /> : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default UsageLicenseDeniedMembersTable
